/**
 * Global component registration.
 * All components registered here are available without explicit imports.
 */

import type { App } from 'vue'

// Vue Select
// @ts-ignore
import vSelect from 'vue-select'

// PrimeVue Components
import Button from 'primevue/button'
import Avatar from 'primevue/avatar'
import Menu from 'primevue/menu'
import PanelMenu from 'primevue/panelmenu'
import Toolbar from 'primevue/toolbar'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import Card from 'primevue/card'
import ConfirmDialog from 'primevue/confirmdialog'
import Column from 'primevue/column'
import Message from 'primevue/message'
import ProgressSpinner from 'primevue/progressspinner'
import Dialog from 'primevue/dialog'
import Tag from 'primevue/tag'
import SpeedDial from 'primevue/speeddial'
import Timeline from '../ui/Timeline.vue'
import Badge from '../ui/Badge.vue'
import KeyValueTag from '../ui/KeyValueTag.vue'
import Dropdown from 'primevue/dropdown'
import InputMask from 'primevue/inputmask'
import Checkbox from 'primevue/checkbox'
import Calendar from 'primevue/calendar'
import Paginator from 'primevue/paginator'
import Textarea from 'primevue/textarea'

// UI Components
import Box from '../ui/Box.vue'
import KpiProgressBar from '../ui/KpiProgressBar.vue'
import FieldsetPanel from '../ui/FieldsetPanel.vue'
import Modal from '../ui/Modal.vue'
import Wizard from '../ui/Wizard.vue'
import WizardModal from '../ui/WizardModal.vue'
import StateMachineWizardModal from '../ui/StateMachineWizardModal.vue'
import WizardModalModern from '../ui/WizardModalModern.vue'
import WizardStepsModern from '../ui/wizard/WizardStepsModern.vue'
import AudioMessage from '../ui/AudioMessage.vue'
import ImageMessage from '../ui/ImageMessage.vue'
import ImageGallery from '../components/ImageGallery.vue'
import TabDescriptor from '../components/TabDescriptor.vue'
import JwtDebugPanel from '../components/JwtDebugPanel.vue'
import PaymentStatusKPIs from '../components/payment-status/PaymentStatusKPIs.vue'
import PaymentStatusTable from '../components/payment-status/PaymentStatusTable.vue'
import Charts from '../ui/Charts.vue'
import Sidebar from '../ui/Sidebar.vue'
import TopBar from '../ui/TopBar.vue'
import MenuItem from '../ui/MenuItem.vue'
import TabView from '../ui/TabView.vue'
import TabViewPanel from '../ui/TabViewPanel.vue'
import AppButton from '../ui/AppButton.vue'
import InfoTip from '../ui/InfoTip.vue'
import InlineMessage from '../ui/InlineMessage.vue'
import Spinner from '../ui/Spinner.vue'
import StatusTag from '../ui/StatusTag.vue'
import Chip from '../ui/Chip.vue'
import ProgressBar from '../ui/ProgressBar.vue'
import Panel from '../ui/Panel.vue'
import Divider from '../ui/Divider.vue'

// Input Components
import InputText from '../inputs/InputText.vue'
import InputTextArea from '../inputs/InputTextArea.vue'
import InputSignature from '../inputs/InputSignature.vue'
import InputQrcode from '../inputs/InputQrcode.vue'
import InputNumber from '../inputs/InputNumber.vue'
import InputDate from '../inputs/InputDate.vue'
import InputDateTime from '../inputs/InputDateTime.vue'
import DateRange from '../inputs/DateRange.vue'
import FileModel from '../inputs/FileModel.vue'
import SelectList from '../inputs/SelectList.vue'
import SelectListMultiple from '../inputs/SelectListMultiple.vue'
import SearchAutocomplete from '../inputs/SearchAutocomplete.vue'
import ButtonsList from '../inputs/ButtonsList.vue'
import ButtonsGrid from '../inputs/ButtonsGrid.vue'
import ButtonListMultiple from '../inputs/ButtonListMultiple.vue'
import InputCurrency from '../inputs/InputCurrency.vue'
import InputPhone from '../inputs/InputPhone.vue'
import InputDocument from '../inputs/InputDocument.vue'
import InputRichText from '../inputs/InputRichText.vue'
import ColorPicker from '../inputs/ColorPicker.vue'
import FranchiseSelect from '../inputs/FranchiseSelect.vue'
import FranchiseSelectMultiple from '../inputs/FranchiseSelectMultiple.vue'
import LocationSelect from '../inputs/LocationSelect.vue'
import Toggle from '../inputs/Toggle.vue'
import InputToggle from '../inputs/InputToggle.vue'
import InputPassword from '../inputs/InputPassword.vue'
import InputWriteOnlySecret from '../inputs/InputWriteOnlySecret.vue'
import InputSlider from '../inputs/InputSlider.vue'

// Grid Components
import DataTable from '../grid/DataTable.vue'
import PdfExportButton from '../grid/exports/PdfExportButton.vue'
import CsvExportButton from '../grid/exports/CsvExportButton.vue'
import ClipboardCopyButton from '../grid/exports/ClipboardCopyButton.vue'
import PrintButton from '../grid/exports/PrintButton.vue'

// Infrastructure Components
import GlobalMessages from '../ui/GlobalMessages.vue'
import ObjectCompletion from '../inputs/ObjectCompletion.vue'

// Filtro Components
import FiltroModal from '../domains/filtro/FiltroModal.vue'
import FiltroHeaderLinks from '../domains/filtro/FiltroHeaderLinks.vue'
import FiltroPage from '../domains/filtro/FiltroPage.vue'

// Occurrences Domain
import OccurrencesPage from '../domains/occurrences/OccurrencesPage.vue'
import OccurrencesModal from '../domains/occurrences/OccurrencesModal.vue'

// Despesas Domain
import DespesasPage from '../domains/despesas/DespesasPage.vue'

// Fechamentos Domain (Plano de Contas)
import FechamentosPage from '../domains/fechamentos/FechamentosPage.vue'

// Plano de Contas Template Management
import PlanoContasPage from '../domains/plano-contas/PlanoContasPage.vue'
import PlanoContasTable from '../domains/plano-contas/PlanoContasTable.vue'
import PlanoContasModal from '../domains/plano-contas/PlanoContasModal.vue'
import PlanoContasForm from '../domains/plano-contas/PlanoContasForm.vue'

// Cliente Domain
import ClientesSimplesPage from '../domains/cliente/ClientesSimplesPage.vue'

// Compromisso Domain
import CompromissosDoOrcamentoTable from '../domains/compromisso/CompromissosDoOrcamentoTable.vue'
import CompromissoPage from '../domains/compromisso/CompromissoPage.vue'

// Recebimento Domain
import RecebimentosCsvImportModal from '../domains/recebimento/RecebimentosCsvImportModal.vue'

// CompromissoServico Domain
import CompromissoServicoPage from '../domains/compromissoservico/CompromissoServicoPage.vue'

// Orcamento Domain
import OrcamentosPage from '../domains/orcamento/OrcamentosPage.vue'
import OrcamentosDoClienteTable from '../domains/orcamento/OrcamentosDoClienteTable.vue'

// OrcamentoServico Domain
import OrcamentoServicoPage from '../domains/orcamentoservico/OrcamentoServicoPage.vue'

// Contato Fidelizacao Domain
import ContatoFidelizacaoForm from '../domains/contato_fidelizacao/ContatoFidelizacaoForm.vue'
import ContatoFidelizacaoModal from '../domains/contato_fidelizacao/ContatoFidelizacaoModal.vue'
import ContatoFidelizacaoTable from '../domains/contato_fidelizacao/ContatoFidelizacaoTable.vue'

// Dashboard Domain
import DashboardPage from '../domains/dashboard/DashboardPage.vue'
// Tab files are imported via defineAsyncComponent inside DashboardPage, so
// they are intentionally NOT registered globally. Widget shells are imported
// directly by their parent tab/page files (skeleton-only Phase 2; Phase 3
// wires real data).

// Agenda Domain
import AgendaPage from '../domains/agenda/AgendaPage.vue'
import CheckinWizardModal from '../domains/agenda/CheckinWizardModal.vue'

// Devices Franquias Domain
import DevicesFranquiasPage from '../domains/devices_franquias/DevicesFranquiasPage.vue'

// Instagram Accounts Domain
import InstagramAccountsPage from '../domains/instagram_accounts/InstagramAccountsPage.vue'
import InstagramAccountsTable from '../domains/instagram_accounts/InstagramAccountsTable.vue'
import InstagramAccountsModal from '../domains/instagram_accounts/InstagramAccountsModal.vue'
import InstagramAccountsForm from '../domains/instagram_accounts/InstagramAccountsForm.vue'
import InstagramOAuthCallback from '../domains/instagram_accounts/InstagramOAuthCallback.vue'

// Messaging Inbox Domain
import MessagingInboxPage from '../domains/messaging_inbox/MessagingInboxPage.vue'

// Messaging Analytics Domain
import MessagingAnalyticsPage from '../domains/messaging_analytics/MessagingAnalyticsPage.vue'

// MensagensProgramadas Domain
import MensagensProgramadasPage from '../domains/mensagens_programadas/MensagensProgramadasPage.vue'

// MensagensProgramadasLote Domain
import MensagensProgramadasLotePage from '../domains/mensagens_programadas_lote/MensagensProgramadasLotePage.vue'
import MensagensProgramadasLoteProgress from '../domains/mensagens_programadas_lote/MensagensProgramadasLoteProgress.vue'

// WhatsApp Message Templates Domain
import WhatsappMessageTemplatesPage from '../domains/whatsapp_message_templates/WhatsappMessageTemplatesPage.vue'

// DespesasCategorias Domain
import DespesasCategoriasPage from '../domains/despesas_categorias/DespesasCategoriasPage.vue'
import DespesasCategoriasTable from '../domains/despesas_categorias/DespesasCategoriasTable.vue'
import DespesasCategoriasModal from '../domains/despesas_categorias/DespesasCategoriasModal.vue'
import DespesasCategoriasForm from '../domains/despesas_categorias/DespesasCategoriasForm.vue'

// DespesasStatus Domain
import DespesasStatusPage from '../domains/despesas_status/DespesasStatusPage.vue'
import DespesasStatusTable from '../domains/despesas_status/DespesasStatusTable.vue'
import DespesasStatusModal from '../domains/despesas_status/DespesasStatusModal.vue'

// ServicosCategorias Domain
import ServicosCategoriasPage from '../domains/servicos_categorias/ServicosCategoriasPage.vue'

// Users Domain
import UsersPage from '../domains/users/UsersPage.vue'
import UsersTable from '../domains/users/UsersTable.vue'
import UserWizard from '../domains/users/UserWizard.vue'
import UserModal from '../domains/users/UserModal.vue'
import UserForm from '../domains/users/UserForm.vue'
import ChangePasswordModal from '../domains/users/ChangePasswordModal.vue'
import UserOnboardingWizard from '../domains/users/onboarding/UserOnboardingWizard.vue'
import OnboardingStep0Registration from '../domains/users/onboarding/OnboardingStep0Registration.vue'
import OnboardingStep1Permissions from '../domains/users/onboarding/OnboardingStep1Permissions.vue'
import OnboardingStep2Franchises from '../domains/users/onboarding/OnboardingStep2Franchises.vue'
import OnboardingStep3ProfilePic from '../domains/users/onboarding/OnboardingStep3ProfilePic.vue'

// Servicos Domain
import ServicosPage from '../domains/servicos/ServicosPage.vue'

// PDFElements Domain
import PDFElementsPage from '../domains/pdf_elements/PDFElementsPage.vue'

// Galeria Domain
import GaleriaPage from '../domains/galeria/GaleriaPage.vue'
import GaleriaTable from '../domains/galeria/GaleriaTable.vue'
import ImageViewerModal from '../domains/galeria/ImageViewerModal.vue'

// Logs Domain
import LogsPage from '../domains/logs/LogsPage.vue'
import LogsTable from '../domains/logs/LogsTable.vue'

// WebhookLogs Domain
import WebhookLogsTable from '../domains/webhook-logs/WebhookLogsTable.vue'

// FormasPagamento Domain
import FormasPagamentoPage from '../domains/formas-pagamento/FormasPagamentoPage.vue'

// FiscalConfig Domain (subaccounts)
import FiscalConfigPage from '../domains/subaccounts/fiscal-config/FiscalConfigPage.vue'
import FiscalConfigTable from '../domains/subaccounts/fiscal-config/FiscalConfigTable.vue'
import FiscalConfigForm from '../domains/subaccounts/fiscal-config/FiscalConfigForm.vue'
// FiscalConfigModal is registered in ModalManager.vue — not globally.

// Invoices Domain (subaccounts) — emitted-invoice listing
import InvoicesPage from '../domains/subaccounts/invoices/InvoicesPage.vue'
import InvoicesTable from '../domains/subaccounts/invoices/InvoicesTable.vue'
import InvoicesDoClienteTable from '../domains/subaccounts/invoices/InvoicesDoClienteTable.vue'
// InvoicesModal is registered in ModalManager.vue — not globally.

// Origens Domain
import OrigensPage from '../domains/origens/OrigensPage.vue'
import OrigensTable from '../domains/origens/OrigensTable.vue'
import OrigensModal from '../domains/origens/OrigensModal.vue'
import OrigensForm from '../domains/origens/OrigensForm.vue'
import SiteUnidadesPage from '../domains/site-unidades/SiteUnidadesPage.vue'
import SiteUnidadesTable from '../domains/site-unidades/SiteUnidadesTable.vue'
import SiteUnidadesModal from '../domains/site-unidades/SiteUnidadesModal.vue'
import SiteUnidadesForm from '../domains/site-unidades/SiteUnidadesForm.vue'
import SiteUnidadesPhotoPicker from '../domains/site-unidades/SiteUnidadesPhotoPicker.vue'
import SiteLeadsPage from '../domains/site-leads/SiteLeadsPage.vue'
import SiteLeadsTable from '../domains/site-leads/SiteLeadsTable.vue'
import SiteLeadsModal from '../domains/site-leads/SiteLeadsModal.vue'
import SiteTemplatesPage from '../domains/site-templates/SiteTemplatesPage.vue'
import SiteTemplatesTable from '../domains/site-templates/SiteTemplatesTable.vue'
import SiteTemplatesModal from '../domains/site-templates/SiteTemplatesModal.vue'
import SiteTemplatesForm from '../domains/site-templates/SiteTemplatesForm.vue'

// Contas Domain
import ContasPage from '../domains/contas/ContasPage.vue'

// Franquias Domain
import FranquiasPage from '../domains/franquias/FranquiasPage.vue'
import FranquiasStatsBox from '../domains/franquias/FranquiasStatsBox.vue'
import FranquiasTable from '../domains/franquias/FranquiasTable.vue'
import FranquiasLocationsTab from '../domains/franquias/FranquiasLocationsTab.vue'
import LocationsListView from '../domains/franquias/LocationsListView.vue'
import LocationsSelectionModal from '../domains/franquias/LocationsSelectionModal.vue'
import FranquiasConfigTab from '../domains/franquias/FranquiasConfigTab.vue'

// Relatorios Domain
import VendasOperadoresPage from '../domains/relatorios/VendasOperadoresPage.vue'
import VendasOperadoresTable from '../domains/relatorios/VendasOperadoresTable.vue'
import RecebimentoOperadoresPage from '../domains/relatorios/RecebimentoOperadoresPage.vue'
import RecebimentoOperadoresTable from '../domains/relatorios/RecebimentoOperadoresTable.vue'
import ComparativoServicosPage from '../domains/relatorios/ComparativoServicosPage.vue'
import VendasProdutosPage from '../domains/relatorios/VendasProdutosPage.vue'
import VendasProdutosTable from '../domains/relatorios/VendasProdutosTable.vue'
import VendasServicosPage from '../domains/relatorios/VendasServicosPage.vue'
import RelatorioOSPage from '../domains/relatorios-os/RelatorioOSPage.vue'
import RelatorioOSTable from '../domains/relatorios-os/RelatorioOSTable.vue'
import ComparativoServicosTable from '../domains/relatorios/ComparativoServicosTable.vue'
import ComparativoFranquiasPage from '../domains/relatorios/ComparativoFranquiasPage.vue'
import ComparativoFranquiasTable from '../domains/relatorios/ComparativoFranquiasTable.vue'
import PerformanceFranquiasPage from '../domains/relatorios/PerformanceFranquiasPage.vue'
import PerformanceFranquiasTable from '../domains/relatorios/PerformanceFranquiasTable.vue'
import ResultadoFinanceiroPage from '../domains/relatorios/ResultadoFinanceiroPage.vue'
import ResultadoFinanceiroTable from '../domains/relatorios/ResultadoFinanceiroTable.vue'
import ServicosRealizadosPage from '../domains/relatorios/ServicosRealizadosPage.vue'
import ServicosRealizadosTable from '../domains/relatorios/ServicosRealizadosTable.vue'
import RelatorioClientesPage from '../domains/relatorios/RelatorioClientesPage.vue'
import RelatorioClientesTable from '../domains/relatorios/RelatorioClientesTable.vue'

// Payroll Domain - Unified structure
import PayrollDashboard from '../domains/payroll/PayrollDashboard.vue'
import FinancePeriodsPage from '../domains/payroll/FinancePeriodsPage.vue'

// Payroll - Periods (Folha de Pagamento)
import PayrollPage from '../domains/payroll/periods/PayrollPage.vue'
import PayrollModal from '../domains/payroll/periods/PayrollModal.vue'
import PayrollTable from '../domains/payroll/periods/PayrollTable.vue'
import EmployeePayrollTable from '../domains/payroll/periods/EmployeePayrollTable.vue'
import CreatePeriodModal from '../domains/payroll/periods/CreatePeriodModal.vue'
import ReceiptUploadDialog from '../domains/payroll/periods/ReceiptUploadDialog.vue'

// Payroll - Refunds (Reembolsos de Despesas)
import PayrollRefundsPage from '../domains/payroll/refunds/PayrollRefundsPage.vue'
import PayrollRefundsModal from '../domains/payroll/refunds/PayrollRefundsModal.vue'
import PayrollRefundsTable from '../domains/payroll/refunds/PayrollRefundsTable.vue'
import PayrollRefundsStatusBadge from '../domains/payroll/refunds/PayrollRefundsStatusBadge.vue'
import PayrollRefundsAcceptWizard from '../domains/payroll/refunds/PayrollRefundsAcceptWizard.vue'

// Payroll - Advances (Adiantamentos Salariais)
import EmployeeAdvancesPage from '../domains/payroll/advances/EmployeeAdvancesPage.vue'
import EmployeeAdvancesTable from '../domains/payroll/advances/EmployeeAdvancesTable.vue'

// Payroll - Employee Deductions (Deduções de Colaboradores)
import EmployeeDeductionsPage from '../domains/payroll/deductions/EmployeeDeductionsPage.vue'
import EmployeeDeductionsModal from '../domains/payroll/deductions/EmployeeDeductionsModal.vue'
import EmployeeDeductionsTable from '../domains/payroll/deductions/EmployeeDeductionsTable.vue'

// Payroll - Payment Rules (Regras de Pagamento)
import PaymentRulesPage from '../domains/payroll/payment-rules/PaymentRulesPage.vue'
import PaymentRulesTable from '../domains/payroll/payment-rules/PaymentRulesTable.vue'
import PaymentRulesModal from '../domains/payroll/payment-rules/PaymentRulesModal.vue'
import PaymentRulesForm from '../domains/payroll/payment-rules/PaymentRulesForm.vue'
import Step1Franchise from '../domains/payroll/payment-rules/wizard-steps/Step1Franchise.vue'
import Step2Employee from '../domains/payroll/payment-rules/wizard-steps/Step2Employee.vue'
import Step3RuleType from '../domains/payroll/payment-rules/wizard-steps/Step3RuleType.vue'
import Step4Calculation from '../domains/payroll/payment-rules/wizard-steps/Step4Calculation.vue'
import Step5Review from '../domains/payroll/payment-rules/wizard-steps/Step5Review.vue'
import RulePreviewPanel from '../domains/payroll/payment-rules/wizard-steps/RulePreviewPanel.vue'

// Payroll - Wizard Components
import Step1PeriodConfig from '../domains/payroll/wizard/Step1PeriodConfig.vue'
import Step2RulesPreview from '../domains/payroll/wizard/Step2RulesPreview.vue'
import Step3RevenueConciliationSummary from '../domains/payroll/wizard/Step3RevenueConciliationSummary.vue'
import RevenueConciliationWizard from '../domains/payroll/wizard/RevenueConciliationWizard.vue'
import Step4ExpenseConciliationSummary from '../domains/payroll/wizard/Step4ExpenseConciliationSummary.vue'
import ExpenseConciliationWizard from '../domains/payroll/wizard/ExpenseConciliationWizard.vue'
import StepCashRecebimentos from '../domains/payroll/wizard/StepCashRecebimentos.vue'
import StepEmployeeAdvances from '../domains/payroll/wizard/StepEmployeeAdvances.vue'
import StepEmployeeDeductions from '../domains/payroll/wizard/StepEmployeeDeductions.vue'
import EmployeeAdvancesConciliationWizard from '../domains/payroll/wizard/EmployeeAdvancesConciliationWizard.vue'
import EmployeeDeductionsConciliationWizard from '../domains/payroll/wizard/EmployeeDeductionsConciliationWizard.vue'
import StepPayrollRefunds from '../domains/payroll/wizard/StepPayrollRefunds.vue'
import Step5PaymentRulesConciliationSummary from '../domains/payroll/wizard/Step5PaymentRulesConciliationSummary.vue'
import StepPayrollSummary from '../domains/payroll/wizard/StepPayrollSummary.vue'
import StepFinish from '../domains/payroll/wizard/StepFinish.vue'
import CashRecebimentosSummary from '../domains/payroll/wizard/CashRecebimentosSummary.vue'
import CashRecebimentosConciliationWizard from '../domains/payroll/wizard/CashRecebimentosConciliationWizard.vue'
import PayrollRefundsSummary from '../domains/payroll/wizard/PayrollRefundsSummary.vue'
import Step6Confirmation from '../domains/payroll/wizard/Step6Confirmation.vue'

// Payroll - Wizard Shared Components
import EmployeeDiscountCard from '../domains/payroll/wizard/shared/EmployeeDiscountCard.vue'
import PayrollConciliationStatusCard from '../domains/payroll/wizard/shared/PayrollConciliationStatusCard.vue'
import CashAdvancesSelectionDialog from '../domains/payroll/wizard/shared/CashAdvancesSelectionDialog.vue'

// Payroll - Dialogs
import PayrollCalculationsDialog from '../domains/payroll/dialogs/PayrollCalculationsDialog.vue'
import CalculationsProgressView from '../domains/payroll/dialogs/CalculationsProgressView.vue'
import CalculationsResultsView from '../domains/payroll/dialogs/CalculationsResultsView.vue'

// Employee Payments Domain
import EmployeePaymentsPage from '../domains/employee-payments/EmployeePaymentsPage.vue'
import EmployeePaymentsTable from '../domains/employee-payments/EmployeePaymentsTable.vue'
import EmployeePaymentDetail from '../domains/employee-payments/EmployeePaymentDetail.vue'

// Deductions Domain
import DeductionsForm from '../domains/deductions/DeductionsForm.vue'
import DeductionsTable from '../domains/deductions/DeductionsTable.vue'

// Franchises Subaccounts Domain (Payment Provider Integration)
import FranchisesSubaccountsPage from '../domains/subaccounts/franchises-subaccounts/FranchisesSubaccountsPage.vue'
import FranchisesSubaccountsTable from '../domains/subaccounts/franchises-subaccounts/FranchisesSubaccountsTable.vue'
import FranchisesSubaccountsInvoiceStatusBadge from '../domains/subaccounts/franchises-subaccounts/FranchisesSubaccountsInvoiceStatusBadge.vue'
import FranchisesSubaccountsLinkButton from '../domains/subaccounts/franchises-subaccounts/FranchisesSubaccountsLinkButton.vue'
import FranchisesSubaccountsForm from '../domains/subaccounts/franchises-subaccounts/wizard/FranchisesSubaccountsForm.vue'
import FranchisesSubaccountsConfigForm from '../domains/subaccounts/franchises-subaccounts/wizard/FranchisesSubaccountsConfigForm.vue'

// SO (Service Order) Domain Pages
import SOServiceCategoriesPage from '../domains/so-service-categories/SOServiceCategoriesPage.vue'
import SOCategoryReportAttributesPicker from '../domains/so-service-categories/SOCategoryReportAttributesPicker.vue'
import SOProductCategoriesPage from '../domains/so-product-categories/SOProductCategoriesPage.vue'
import SOPrioritiesPage from '../domains/so-priorities/SOPrioritiesPage.vue'
import SOCatalogFiscalPage from '../domains/so-catalog-fiscal/SOCatalogFiscalPage.vue'
import SOServicesPage from '../domains/so-services/SOServicesPage.vue'
import SOServicePrioritiesPage from '../domains/so-service-priorities/SOServicePrioritiesPage.vue'
import SOAdditionalServicePricesPage from '../domains/so-additional-service-prices/SOAdditionalServicePricesPage.vue'
import SOStockPage from '../domains/so-stock/SOStockPage.vue'
import SOOrdersPage from '../domains/so-orders/SOOrdersPage.vue'
import SOAdditionalServicesPage from '../domains/so-additional-services/SOAdditionalServicesPage.vue'
import SOProductionStepsPage from '../domains/so-production-steps/SOProductionStepsPage.vue'
import SOOrdersDoClienteTable from '../domains/so-orders/SOOrdersDoClienteTable.vue'
import SOEntryReportPage from '../domains/so-orders/SOEntryReportPage.vue'
import PDVExitReportPage from '../domains/pdv/PDVExitReportPage.vue'
import SOReprocessoSection from '../domains/so-orders/SOReprocessoSection.vue'
import ProductionHistoryTimeline from '../domains/pdv/scanner/ProductionHistoryTimeline.vue'
import SODeliveryDateEditor from '../domains/so-orders/SODeliveryDateEditor.vue'
import SODeliveryDateChangeForm from '../domains/so-orders/SODeliveryDateChangeForm.vue'
import SODeliveryDateChangeModal from '../domains/so-orders/SODeliveryDateChangeModal.vue'
import SOWorkOrderStepsView from '../domains/so-orders/SOWorkOrderStepsView.vue'
import SOPickupNotificationsView from '../domains/so-orders/SOPickupNotificationsView.vue'
import SOOrderItemDetailModal from '../domains/so-orders/item-detail/SOOrderItemDetailModal.vue'
import SOItemGeralTab from '../domains/so-orders/item-detail/SOItemGeralTab.vue'
import SOItemLaudosTab from '../domains/so-orders/item-detail/SOItemLaudosTab.vue'
import SOItemLaudoEntradaTab from '../domains/so-orders/item-detail/SOItemLaudoEntradaTab.vue'
import SOItemLaudoSaidaTab from '../domains/so-orders/item-detail/SOItemLaudoSaidaTab.vue'
import SOItemProducaoTab from '../domains/so-orders/item-detail/SOItemProducaoTab.vue'
import SOItemSolicitacoesTab from '../domains/so-orders/item-detail/SOItemSolicitacoesTab.vue'
import SOItemEntregaTab from '../domains/so-orders/item-detail/SOItemEntregaTab.vue'
import SOLaudosAnalysisPage from '../domains/so-orders/SOLaudosAnalysisPage.vue'
import SOLaudosAnalysisTable from '../domains/so-orders/SOLaudosAnalysisTable.vue'

// SO Report Attributes Domain
import SOReportAttributesPage from '../domains/so-report-attributes/SOReportAttributesPage.vue'
import SOReportAttributesModal from '../domains/so-report-attributes/SOReportAttributesModal.vue'
import SOReportAttributesForm from '../domains/so-report-attributes/SOReportAttributesForm.vue'
import SOReportAttributesTable from '../domains/so-report-attributes/SOReportAttributesTable.vue'
import PDVLaudosEntradaPage from '../domains/pdv/PDVLaudosEntradaPage.vue'
import PDVEntregasKpiCards from '../domains/pdv/PDVEntregasKpiCards.vue'
import PDVEntregasNotifyModal from '../domains/pdv/PDVEntregasNotifyModal.vue'
import PDVEntregasDeliveryModal from '../domains/pdv/PDVEntregasDeliveryModal.vue'
import PDVCheckoutRecebimentosPanel from '../domains/pdv/PDVCheckoutRecebimentosPanel.vue'
import PDVCheckoutInvoicesPanel from '../domains/pdv/PDVCheckoutInvoicesPanel.vue'
import VendasKpiPage from '../domains/vendas-kpi/VendasKpiPage.vue'
import ProducaoKpiPage from '../domains/producao-kpi/ProducaoKpiPage.vue'
import VendasProducaoPage from '../domains/vendas-producao/VendasProducaoPage.vue'
import SOOrderItemRequestsEditor from '../domains/pdv/wizards/SOOrderItemRequestsEditor.vue'
import SOOrderItemRequestsVerifier from '../domains/pdv/wizards/SOOrderItemRequestsVerifier.vue'
import SOProductSelector from '../domains/so/components/SOProductSelector.vue'
import CameraCapture from '../components/CameraCapture.vue'

// Anticipations Domain (Payment Receivable Anticipation)
import AnticipationsTable from '../domains/anticipations/AnticipationsTable.vue'
import AnticipationLimitsCard from '../domains/anticipations/AnticipationLimitsCard.vue'
import AnticipationConfigCard from '../domains/anticipations/AnticipationConfigCard.vue'
import AnticipationStatusBadge from '../domains/anticipations/AnticipationStatusBadge.vue'

// Translations Admin Domain
import TranslationsPage from '../domains/translations/TranslationsPage.vue'
import TranslationsTable from '../domains/translations/TranslationsTable.vue'
import TranslationsModal from '../domains/translations/TranslationsModal.vue'
import TranslationsForm from '../domains/translations/TranslationsForm.vue'

// Languages Admin Domain
import LanguagesPage from '../domains/languages/LanguagesPage.vue'
import LanguagesTable from '../domains/languages/LanguagesTable.vue'
import LanguagesModal from '../domains/languages/LanguagesModal.vue'
import LanguagesForm from '../domains/languages/LanguagesForm.vue'

// LGPD Registros Admin Domain
import LgpdRegistrosPage from '../domains/lgpd/LgpdRegistrosPage.vue'
import LgpdTermosTable from '../domains/lgpd/LgpdTermosTable.vue'
import LgpdTermoModal from '../domains/lgpd/LgpdTermoModal.vue'
import LgpdTermoForm from '../domains/lgpd/LgpdTermoForm.vue'
import LgpdAceitesTable from '../domains/lgpd/LgpdAceitesTable.vue'
import LgpdAceiteDetalheModal from '../domains/lgpd/LgpdAceiteDetalheModal.vue'

// Subscription domain
import SubscriptionBlockedPage from '../domains/subscription/SubscriptionBlockedPage.vue'
import MyFranchisesPage from '../domains/subscription/MyFranchisesPage.vue'
import MyFranchisesTable from '../domains/subscription/MyFranchisesTable.vue'
import SubscribeModal from '../domains/subscription/SubscribeModal.vue'
import SubscriptionDetailModal from '../domains/subscription/SubscriptionDetailModal.vue'

// Staff Credits console (Full-gated; cost/margin/COGS ONLY here)
import StaffCreditsPage from '../domains/staff/credits/StaffCreditsPage.vue'
import StaffCreditsKpiCards from '../domains/staff/credits/StaffCreditsKpiCards.vue'
import StaffCreditsTable from '../domains/staff/credits/StaffCreditsTable.vue'
import CreditPacksTable from '../domains/staff/credits/packs/CreditPacksTable.vue'
import CreditPackForm from '../domains/staff/credits/packs/CreditPackForm.vue'
import CreditPackModal from '../domains/staff/credits/packs/CreditPackModal.vue'
import CreditPackMarketsPanel from '../domains/staff/credits/packs/CreditPackMarketsPanel.vue'
import ProviderCostForm from '../domains/staff/credits/ProviderCostForm.vue'
import PegOverridesTable from '../domains/staff/credits/PegOverridesTable.vue'
import GlobalPegForm from '../domains/staff/credits/GlobalPegForm.vue'
import ReserveSettingsForm from '../domains/staff/credits/ReserveSettingsForm.vue'
import PegOverrideForm from '../domains/staff/credits/PegOverrideForm.vue'
import PricingHistoryTable from '../domains/staff/credits/PricingHistoryTable.vue'
import CreditPurchasesTable from '../domains/staff/credits/CreditPurchasesTable.vue'

// Staff Grace Periods console (Full/Master-gated)
import GracePeriodsPage from '../domains/staff/grace/GracePeriodsPage.vue'
import GracePeriodsTable from '../domains/staff/grace/GracePeriodsTable.vue'
import GraceGrantModal from '../domains/staff/grace/GraceGrantModal.vue'
import GraceRevokeModal from '../domains/staff/grace/GraceRevokeModal.vue'

// Staff Transactions console (Full-gated)
import StaffTransactionsPage from '../domains/staff/transactions/StaffTransactionsPage.vue'
import StaffTransactionsTable from '../domains/staff/transactions/StaffTransactionsTable.vue'
// Payment Gateways console (Full-only) — F6
import PaymentGatewaysPage from '../domains/staff/gateways/PaymentGatewaysPage.vue'
import GatewayTestDevTrigger from '../domains/staff/gateways/GatewayTestDevTrigger.vue'

// Assistant (AI agent)
import AssistantDashboard from '../domains/assistant/AssistantDashboard.vue'
import AssistantCreditTiles from '../domains/assistant/AssistantCreditTiles.vue'
import AgentHealthPanel from '../domains/assistant/AgentHealthPanel.vue'
import AgentStatsPanel from '../domains/assistant/AgentStatsPanel.vue'
import AgentAuditPanel from '../domains/assistant/AgentAuditPanel.vue'
import AssistantChat from '../domains/assistant/AssistantChat.vue'
import AssistantChatList from '../domains/assistant/AssistantChatList.vue'
import AssistantMessageDiagnostics from '../domains/assistant/AssistantMessageDiagnostics.vue'

import { log } from '../utils/DebugLogger'

/**
 * All global components mapped by name
 */
const components = {
  // Third-party
  'v-select': vSelect,

  // PrimeVue
  Button,
  SpeedDial,
  Avatar,
  Menu,
  PanelMenu,
  Toolbar,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Accordion,
  AccordionTab,
  AccordionPanel,
  AccordionHeader,
  AccordionContent,
  Card,
  ConfirmDialog,
  Column,
  Message,
  ProgressSpinner,
  Dialog,
  Tag,
  Timeline,
  Badge,
  KeyValueTag,
  Dropdown,
  InputMask,
  Checkbox,
  Calendar,
  Paginator,
  Textarea,

  // UI
  Box,
  KpiProgressBar,
  FieldsetPanel,
  Modal,
  Wizard,
  ImageGallery,
  TabDescriptor,
  JwtDebugPanel,
  PaymentStatusKPIs,
  PaymentStatusTable,
  WizardModal,
  StateMachineWizardModal,
  WizardModalModern,
  WizardStepsModern,
  AudioMessage,
  ImageMessage,
  Charts,
  Sidebar,
  TopBar,
  MenuItem,
  TabView,
  TabViewPanel,
  AppButton,
  InfoTip,
  InlineMessage,
  Spinner,
  StatusTag,
  Chip,
  ProgressBar,
  Panel,
  Divider,

  // Inputs
  InputText,
  InputTextArea,
  InputSignature,
  InputQrcode,
  InputNumber,
  InputDate,
  InputDateTime,
  DateRange,
  FileModel,
  SelectList,
  SelectListMultiple,
  SearchAutocomplete,
  ButtonsList,
  ButtonsGrid,
  ButtonListMultiple,
  InputCurrency,
  InputPhone,
  InputDocument,
  InputRichText,
  ColorPicker,
  FranchiseSelect,
  FranchiseSelectMultiple,
  LocationSelect,
  Toggle,
  InputToggle,
  InputPassword,
  InputWriteOnlySecret,
  InputSlider,

  // Grid
  DataTable,
  PdfExportButton,
  CsvExportButton,
  ClipboardCopyButton,
  PrintButton,

  // Infrastructure
  GlobalMessages,
  ObjectCompletion,

  // Filtro
  FiltroModal,
  FiltroHeaderLinks,
  FiltroPage,

  // Occurrences
  OccurrencesPage,
  OccurrencesModal,

  // Despesas
  DespesasPage,

  // Fechamentos (Plano de Contas)
  FechamentosPage,

  // Plano de Contas Template
  PlanoContasPage,
  PlanoContasTable,
  PlanoContasModal,
  PlanoContasForm,

  // Cliente
  ClientesSimplesPage,

  // Compromisso
  CompromissosDoOrcamentoTable,
  CompromissoPage,

  // Recebimento
  RecebimentosCsvImportModal,

  // CompromissoServico
  CompromissoServicoPage,

  // Orcamento
  OrcamentosPage,
  OrcamentosDoClienteTable,

  // OrcamentoServico
  OrcamentoServicoPage,

  // Contato Fidelizacao
  ContatoFidelizacaoForm,
  ContatoFidelizacaoModal,
  ContatoFidelizacaoTable,

  // Dashboard
  DashboardPage,

  // Agenda
  AgendaPage,
  CheckinWizardModal,

  // Devices Franquias
  DevicesFranquiasPage,

  // Instagram Accounts
  InstagramAccountsPage,
  InstagramAccountsTable,
  InstagramAccountsModal,
  InstagramAccountsForm,
  InstagramOAuthCallback,

  // Messaging Inbox
  MessagingInboxPage,

  // Messaging Analytics
  MessagingAnalyticsPage,

  // MensagensProgramadas
  MensagensProgramadasPage,

  // MensagensProgramadasLote
  MensagensProgramadasLotePage,
  MensagensProgramadasLoteProgress,

  // WhatsApp Message Templates
  WhatsappMessageTemplatesPage,

  // DespesasCategorias
  DespesasCategoriasPage,
  DespesasCategoriasTable,
  DespesasCategoriasModal,
  DespesasCategoriasForm,

  // DespesasStatus
  DespesasStatusPage,
  DespesasStatusTable,
  DespesasStatusModal,

  // ServicosCategorias
  ServicosCategoriasPage,

  // Users
  UsersPage,
  UsersTable,
  UserWizard,
  UserModal,
  UserForm,
  ChangePasswordModal,
  UserOnboardingWizard,
  OnboardingStep0Registration,
  OnboardingStep1Permissions,
  OnboardingStep2Franchises,
  OnboardingStep3ProfilePic,

  // Servicos
  ServicosPage,

  // PDFElements
  PDFElementsPage,

  // Galeria
  GaleriaPage,
  GaleriaTable,
  ImageViewerModal,

  // Logs
  LogsPage,
  LogsTable,

  // WebhookLogs
  WebhookLogsTable,

  // FormasPagamento
  FormasPagamentoPage,

  // FiscalConfig (subaccounts)
  FiscalConfigPage,
  FiscalConfigTable,
  FiscalConfigForm,

  // Invoices (subaccounts)
  InvoicesPage,
  InvoicesTable,
  InvoicesDoClienteTable,

  // Origens
  OrigensPage,
  OrigensTable,
  OrigensModal,
  OrigensForm,

  // Site Unidades
  SiteUnidadesPage,
  SiteUnidadesTable,
  SiteUnidadesModal,
  SiteUnidadesForm,
  SiteUnidadesPhotoPicker,
  SiteLeadsPage,
  SiteLeadsTable,
  SiteLeadsModal,
  SiteTemplatesPage,
  SiteTemplatesTable,
  SiteTemplatesModal,
  SiteTemplatesForm,

  // Contas
  ContasPage,

  // Franquias
  FranquiasPage,
  FranquiasStatsBox,
  FranquiasTable,
  FranquiasLocationsTab,
  LocationsListView,
  LocationsSelectionModal,
  FranquiasConfigTab,

  // Relatorios
  VendasOperadoresPage,
  VendasOperadoresTable,
  RecebimentoOperadoresPage,
  RecebimentoOperadoresTable,
  ComparativoServicosPage,
  ComparativoServicosTable,
  VendasProdutosPage,
  VendasProdutosTable,
  VendasServicosPage,
  RelatorioOSPage,
  RelatorioOSTable,
  ComparativoFranquiasPage,
  ComparativoFranquiasTable,
  PerformanceFranquiasPage,
  PerformanceFranquiasTable,
  ResultadoFinanceiroPage,
  ResultadoFinanceiroTable,
  ServicosRealizadosPage,
  ServicosRealizadosTable,
  RelatorioClientesPage,
  RelatorioClientesTable,

  // Payment Rules
  PaymentRulesPage,
  PaymentRulesTable,
  PaymentRulesModal,
  PaymentRulesForm,
  Step1Franchise,
  Step2Employee,
  Step3RuleType,
  Step4Calculation,
  Step5Review,
  RulePreviewPanel,

  // Payroll
  PayrollDashboard,
  PayrollPage,
  FinancePeriodsPage,
  PayrollModal,
  PayrollTable,
  EmployeePayrollTable,
  CreatePeriodModal,
  ReceiptUploadDialog,
  Step1PeriodConfig,
  Step2RulesPreview,
  Step3RevenueConciliationSummary,
  RevenueConciliationWizard,
  Step4ExpenseConciliationSummary,
  ExpenseConciliationWizard,
  StepCashRecebimentos,
  StepEmployeeAdvances,
  StepEmployeeDeductions,
  EmployeeAdvancesConciliationWizard,
  EmployeeDeductionsConciliationWizard,
  StepPayrollRefunds,
  Step5PaymentRulesConciliationSummary,
  StepPayrollSummary,
  StepFinish,
  CashRecebimentosSummary,
  CashRecebimentosConciliationWizard,
  PayrollRefundsSummary,
  Step6Confirmation,
  PayrollCalculationsDialog,
  CalculationsProgressView,
  CalculationsResultsView,

  // Wizard Shared Components
  EmployeeDiscountCard,
  PayrollConciliationStatusCard,
  CashAdvancesSelectionDialog,

  // Payroll Refunds
  PayrollRefundsPage,
  PayrollRefundsModal,
  PayrollRefundsTable,
  PayrollRefundsStatusBadge,
  PayrollRefundsAcceptWizard,

  // Payroll Advances
  EmployeeAdvancesPage,
  EmployeeAdvancesTable,

  // Payroll Employee Deductions
  EmployeeDeductionsPage,
  EmployeeDeductionsModal,
  EmployeeDeductionsTable,

  // Employee Payments
  EmployeePaymentsPage,
  EmployeePaymentsTable,
  EmployeePaymentDetail,

  // Deductions
  DeductionsForm,
  DeductionsTable,

  // Franchises Subaccounts (Payment Provider Integration)
  FranchisesSubaccountsPage,
  FranchisesSubaccountsTable,
  FranchisesSubaccountsInvoiceStatusBadge,
  FranchisesSubaccountsLinkButton,
  FranchisesSubaccountsForm,
  FranchisesSubaccountsConfigForm,

  // Anticipations (Payment Receivable Anticipation)
  AnticipationsTable,
  AnticipationLimitsCard,
  AnticipationConfigCard,
  AnticipationStatusBadge,

  // SO (Service Order) Domain
  SOServiceCategoriesPage,
  SOCategoryReportAttributesPicker,
  SOProductCategoriesPage,
  SOPrioritiesPage,
  SOCatalogFiscalPage,
  SOServicesPage,
  SOServicePrioritiesPage,
  SOAdditionalServicePricesPage,
  SOStockPage,
  SOOrdersPage,
  SOAdditionalServicesPage,
  SOProductionStepsPage,
  SOOrdersDoClienteTable,
  SOEntryReportPage,
  PDVExitReportPage,
  SOReprocessoSection,
  ProductionHistoryTimeline,
  SODeliveryDateEditor,
  SODeliveryDateChangeForm,
  SODeliveryDateChangeModal,
  SOWorkOrderStepsView,
  SOPickupNotificationsView,
  SOOrderItemDetailModal,
  SOItemGeralTab,
  SOItemLaudosTab,
  SOItemLaudoEntradaTab,
  SOItemLaudoSaidaTab,
  SOItemProducaoTab,
  SOItemSolicitacoesTab,
  SOItemEntregaTab,
  SOLaudosAnalysisPage,
  SOLaudosAnalysisTable,
  SOReportAttributesPage,
  SOReportAttributesModal,
  SOReportAttributesForm,
  SOReportAttributesTable,
  PDVLaudosEntradaPage,
  PDVEntregasKpiCards,
  PDVEntregasNotifyModal,
  PDVEntregasDeliveryModal,
  PDVCheckoutRecebimentosPanel,
  PDVCheckoutInvoicesPanel,
  SOOrderItemRequestsEditor,
  SOOrderItemRequestsVerifier,
  SOProductSelector,
  CameraCapture,

  // Vendas KPI Domain
  VendasKpiPage,

  // Producao KPI Domain
  ProducaoKpiPage,

  // Vendas + Producao combinado
  VendasProducaoPage,

  // Translations Admin Domain
  TranslationsPage,
  TranslationsTable,
  TranslationsModal,
  TranslationsForm,

  // Languages Admin Domain
  LanguagesPage,
  LanguagesTable,
  LanguagesModal,
  LanguagesForm,

  // LGPD Registros Admin Domain
  LgpdRegistrosPage,
  LgpdTermosTable,
  LgpdTermoModal,
  LgpdTermoForm,
  LgpdAceitesTable,
  LgpdAceiteDetalheModal,

  // Subscription domain
  SubscriptionBlockedPage,
  MyFranchisesPage,
  MyFranchisesTable,
  SubscribeModal,
  SubscriptionDetailModal,

  // Staff Credits console (Full-gated; cost/margin/COGS ONLY here)
  StaffCreditsPage,
  StaffCreditsKpiCards,
  StaffCreditsTable,
  CreditPacksTable,
  CreditPackForm,
  CreditPackModal,
  CreditPackMarketsPanel,
  ProviderCostForm,
  PegOverridesTable,
  GlobalPegForm,
  ReserveSettingsForm,
  PegOverrideForm,
  PricingHistoryTable,
  CreditPurchasesTable,

  // Staff Grace Periods console (Full/Master-gated)
  GracePeriodsPage,
  GracePeriodsTable,
  GraceGrantModal,
  GraceRevokeModal,

  // Staff Transactions console (Full-gated)
  StaffTransactionsPage,
  StaffTransactionsTable,

  // Payment Gateways console (Full-only) — F6
  PaymentGatewaysPage,
  GatewayTestDevTrigger,

  // Assistant (AI agent)
  AssistantDashboard,
  AssistantCreditTiles,
  AgentHealthPanel,
  AgentStatsPanel,
  AgentAuditPanel,
  AssistantChat,
  AssistantChatList,
  AssistantMessageDiagnostics,
}

/**
 * Registers all global components with the Vue app
 * @param {Object} app - Vue app instance
 */
export const registerGlobalComponents = (app: App): void => {
  Object.entries(components).forEach(([name, component]) => {
    app.component(name, component)
  })
  log('App', 'bootstrap', `✅ Registered ${Object.keys(components).length} components`)
}
